/**
 * Everything this app asks the API about user administration.
 *
 * Admin-only surface: `/api/v1/admin/*` requires `user.read` just to list,
 * and each mutation its own narrower permission. The only caller of
 * `client` for this slice — routes and components go through `queries.ts`.
 */

import { client } from "@/lib/api/client";
import type { components } from "@/lib/api/schema";

export type RoleGrant = components["schemas"]["RoleGrantOut"];
export type Role = components["schemas"]["Role"];

/** `roles` normalised to always be an array -- the schema types it
 *  optional because the pydantic field carries a default, not because a
 *  caller should have to guard against `undefined` on every access. */
export type AdminUser = Omit<components["schemas"]["UserOut"], "roles"> & { roles: RoleGrant[] };

export async function listUsers(): Promise<AdminUser[]> {
  const users = await client.request<components["schemas"]["UserOut"][]>("/api/v1/admin/users");
  return users.map((user) => ({ ...user, roles: user.roles ?? [] }));
}

/**
 * These three mutation routes return the acted-on user, but never with
 * `roles` populated (see `admin_routes.UserOut`'s docstring) -- callers
 * that need the current grants re-read them from `listUsers` via query
 * invalidation rather than from this response.
 */
type MutatedUser = components["schemas"]["UserOut"];

export function provisionUser(body: {
  email: string;
  display_name?: string;
}): Promise<MutatedUser> {
  return client.request<MutatedUser>("/api/v1/admin/users", { method: "POST", body });
}

export function activateUser(userId: string): Promise<MutatedUser> {
  return client.request<MutatedUser>(`/api/v1/admin/users/${userId}/activate`, { method: "POST" });
}

export function deactivateUser(userId: string): Promise<MutatedUser> {
  return client.request<MutatedUser>(`/api/v1/admin/users/${userId}/deactivate`, {
    method: "POST",
  });
}

/** `master_type_id` omitted or `null` grants globally. */
export function grantRole(
  userId: string,
  body: { role: Role; master_type_id?: string | null },
): Promise<{ grant_id: string }> {
  return client.request<{ grant_id: string }>(`/api/v1/admin/users/${userId}/roles`, {
    method: "POST",
    body,
  });
}

export function revokeGrant(grantId: string): Promise<{ user_id: string; status: string }> {
  return client.request<{ user_id: string; status: string }>(`/api/v1/admin/roles/${grantId}`, {
    method: "DELETE",
  });
}
